/**
 * TabNavigationContext.tsx - Cross-Tab Navigation Context
 *
 * Provides the minimum surface MainLayout_v2 needs to drive tab switches and
 * the auto-switch suppression timestamp. The earlier full TabNavigationProvider
 * (with bespoke navigateToOracleWithContext / navigateToWorkspaceWithContext
 * etc. methods) was never consumed by MainLayout_v2 — contextual cross-tab
 * navigation goes through @services/crossTabNavigation directly. Cleaning up
 * the unused provider + dead methods removes the `as any` cast at the layout
 * level and a couple hundred lines of unreachable code (#44).
 *
 * Per wobbly-nibbling-chipmunk.md Phase 5
 * Per REASONING_ORACLE_PROTOTYPE_SELECTION_v3.md Section 12.3
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type {
  OracleNavigationContext,
  WorkspaceNavigationContext,
  GraphNavigationContext,
  ReportNavigationContext,
} from '@services/crossTabNavigation'
import { getNavigationContext } from '@services/crossTabNavigation'

// ============================================================================
// Types
// ============================================================================

export type CenterTab = 'workspace' | 'report' | 'graph' | 'oracle' | 'document' | 'execution' | 'review' | 'canvas' | 'vigil' | 'dossier' | 'agency'

interface TabNavigationContextType {
  /** User-initiated navigation. Stamps lastUserTabChangeAt for auto-switch suppression. */
  navigateToTab: (tabId: CenterTab) => void

  /** Programmatic navigation. Does NOT stamp lastUserTabChangeAt. */
  navigateToTabSilent: (tabId: CenterTab) => void

  /** Timestamp of the last user-initiated tab change. Used to suppress auto-switch. */
  lastUserTabChangeAt: number | null
}

// ============================================================================
// Context
// ============================================================================

export const TabNavigationContext = createContext<TabNavigationContextType | null>(null)

// ============================================================================
// Hook for consuming the navigation context with a clear missing-provider error
// ============================================================================

export const useTabNavigation = (): TabNavigationContextType => {
  const context = useContext(TabNavigationContext)
  if (!context) {
    throw new Error('useTabNavigation must be used inside a TabNavigationContext.Provider')
  }
  return context
}

// ============================================================================
// Hook for reading navigation context on tab mount
// ============================================================================

/**
 * Hook to read and consume navigation context when a tab mounts.
 * The context is cleared after reading to prevent re-processing.
 *
 * @param tabId - The tab that is mounting
 * @returns The navigation context for this tab, or null
 */
export function useIncomingNavigationContext<T extends CenterTab>(tabId: T) {
  const [incomingContext, setIncomingContext] = useState<
    T extends 'oracle'
      ? OracleNavigationContext | null
      : T extends 'workspace'
        ? WorkspaceNavigationContext | null
        : T extends 'graph'
          ? GraphNavigationContext | null
          : T extends 'report'
            ? ReportNavigationContext | null
            : null
  >(null as any)

  useEffect(() => {
    const navContext = getNavigationContext()
    if (navContext && navContext.tab === tabId) {
      setIncomingContext(navContext.context as any)
    }
  }, [tabId])

  // Clear context after it's been read
  const clearContext = useCallback(() => {
    setIncomingContext(null as any)
  }, [])

  return { incomingContext, clearContext }
}
